import { Component, type ErrorInfo, type ReactNode } from "react";

import { DetailNotice } from "@/pages/sports/components/detail-notice";

interface Props {
  /** Lowercase noun for the log line, e.g. "standings". */
  what: string;
  children: ReactNode;
}

/**
 * Keeps one TINA-owned section from blanking the page around it. When a view schema
 * misses, `parseRead` hands the raw payload on, and a wrongly typed value can throw
 * during render; this catches that and shows the same muted line as an empty section.
 * Give it a `key` that changes with the parent record so a failure does not stick.
 */
export class SectionBoundary extends Component<Props, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[SectionBoundary] ${this.props.what} failed to render`,
      error,
      info.componentStack,
    );
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <DetailNotice
        state={{ status: "empty" }}
        what={this.props.what}
        emptyText="Could not show this section"
      />
    );
  }
}
